import { cn } from '@/lib/cn'
import { ColorInput } from './ColorInput'

export interface FontColorPalette {
  mode: 'palette'
  light_bg: string
  mid_bg?: string
  dark_bg: string
}

interface FontColorInputProps {
  value?: string | FontColorPalette
  onChange: (value: string | FontColorPalette | undefined) => void
}

type Mode = 'auto' | 'fixed' | 'palette'

function resolveMode(value?: string | FontColorPalette): Mode {
  if (!value || value === 'auto') return 'auto'
  if (typeof value === 'object' && value.mode === 'palette') return 'palette'
  return 'fixed'
}

/** Extract a single hex color from the current value, for pre-filling when switching modes */
function extractHex(value?: string | FontColorPalette): string {
  if (!value || value === 'auto') return '#FFFFFF'
  if (typeof value === 'string') return value
  // palette — pick dark_bg as the best single color
  return value.dark_bg || value.light_bg || '#FFFFFF'
}

export function FontColorInput({ value, onChange }: FontColorInputProps) {
  const mode = resolveMode(value)

  const handleModeSwitch = (newMode: Mode) => {
    if (newMode === mode) return

    if (newMode === 'auto') {
      onChange('auto')
      return
    }

    const hex = extractHex(value)

    if (newMode === 'fixed') {
      onChange(hex)
      return
    }

    // palette
    if (typeof value === 'object' && value.mode === 'palette') {
      // Already palette, no-op
      onChange(value)
    } else {
      onChange({
        mode: 'palette',
        light_bg: '#000000',
        dark_bg: hex,
      })
    }
  }

  return (
    <div className="space-y-2">
      {/* Mode selector — matches text_effect button group styling */}
      <div className="inline-flex rounded-md border border-input overflow-hidden">
        {([
          { key: 'auto', label: '自动' },
          { key: 'fixed', label: '固定颜色' },
          { key: 'palette', label: '调色板' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={cn(
              'px-4 py-1.5 text-xs font-medium transition-colors border-r last:border-r-0 border-input',
              mode === key
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
            )}
            onClick={() => handleModeSwitch(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Mode content */}
      {mode === 'auto' && (
        <p className="text-xs text-muted-foreground">(自动检测，根据背景亮度选择最佳颜色)</p>
      )}

      {mode === 'fixed' && (
        <ColorInput
          value={typeof value === 'string' && value !== 'auto' ? value : '#FFFFFF'}
          onChange={v => onChange(v || undefined)}
          placeholder="#FFFFFF"
        />
      )}

      {mode === 'palette' && (() => {
        const palette: FontColorPalette = typeof value === 'object' && value?.mode === 'palette'
          ? value
          : { mode: 'palette', light_bg: '#000000', dark_bg: '#FFFFFF' }

        const updatePalette = (partial: Partial<FontColorPalette>) => {
          onChange({ ...palette, ...partial })
        }

        return (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">亮色背景字色</label>
              <ColorInput
                value={palette.light_bg}
                onChange={v => updatePalette({ light_bg: v || '#000000' })}
                placeholder="#000000"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                中间色背景字色 <span className="text-muted-foreground">(可选)</span>
              </label>
              <ColorInput
                value={palette.mid_bg}
                onChange={v => updatePalette({ mid_bg: v || undefined })}
                placeholder="#666666"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">暗色背景字色</label>
              <ColorInput
                value={palette.dark_bg}
                onChange={v => updatePalette({ dark_bg: v || '#FFFFFF' })}
                placeholder="#FFFFFF"
              />
            </div>
          </div>
        )
      })()}
    </div>
  )
}
